'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { dayjs, DEFAULT_TIMEZONE } from '../libs/dayjs'

interface UseCurrentTimeOptions {
  /** 훅이 활성화된 상태인지 여부 (기본값: true) */
  enabled?: boolean
  /** 업데이트 간격 (ms, 기본값: 1000) */
  interval?: number
  /** 유저 타임존 (기본값: Asia/Seoul) */
  timezone?: string
}

interface UseCurrentTimeReturn {
  /** 현재 시간 (Unix timestamp, 초 단위) */
  currentTime: number
  /** 현재 날짜 문자열 (YYYY-MM-DD 형식, 브라우저 로컬 시간 기준) */
  currentDate: string
  /** 시간 업데이트를 시작하는 함수 */
  start: () => void
  /** 시간 업데이트를 중지하는 함수 */
  stop: () => void
  /** 레슨 입장 가능 여부를 확인하는 함수 (레슨 시작 시간부터 입장 가능) */
  canEnterClass: (classStartTimestamp: number) => boolean
  /** 예약 변경 가능 여부를 확인하는 함수 */
  canChangeReservation: (classStartTimestamp: number, cutoffHours?: number) => boolean
  /** 레슨 시작 여부를 확인하는 함수 */
  isLessonStarted: (classStartTimestamp: number) => boolean
}

/**
 * 현재 시간을 주기적으로 업데이트하는 훅
 *
 * @param options - 훅 옵션
 * @returns 현재 시간 정보와 제어 함수들
 *
 * @example
 * ```tsx
 * function Component() {
 *   const { currentTime, currentDate, canEnterClass } = useCurrentTime()
 *   const lessonStartTime = 1640995200 // 예시 timestamp
 *
 *   return (
 *     <div>
 *       <p>현재 시간: {currentTime}</p>
 *       <p>현재 날짜: {currentDate}</p>
 *       <p>입장 가능: {canEnterClass(lessonStartTime) ? '예' : '아니오'}</p>
 *     </div>
 *   )
 * }
 * ```
 */
export const useCurrentTime = ({
  enabled = true,
  interval = 1000,
  timezone,
}: UseCurrentTimeOptions = {}): UseCurrentTimeReturn => {
  const tz = timezone ?? DEFAULT_TIMEZONE
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [currentDate, setCurrentDate] = useState<string>('')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const enabledRef = useRef(enabled)

  // enabled 상태를 ref로 관리하여 최신 상태 유지
  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  const updateTime = useCallback(() => {
    if (!enabledRef.current) {
      return
    }

    setCurrentTime((prevTime) => {
      const now = dayjs().unix()

      if (prevTime === 0) {
        // 초기 설정: 유저 타임존 기준으로 날짜 설정
        const localDate = dayjs().tz(tz).format('YYYY-MM-DD')
        setCurrentDate(localDate)
      }

      return now
    })
  }, [tz])

  const start = useCallback(() => {
    if (intervalRef.current) {
      return
    } // 이미 실행 중이면 무시

    updateTime() // 즉시 한 번 실행
    intervalRef.current = setInterval(updateTime, interval)
  }, [interval, updateTime])

  const stop = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  // 레슨 입장 가능 여부 (레슨 시작 시간부터 입장 가능)
  const canEnterClass = (classStartTimestamp: number): boolean => {
    const classStartTime = dayjs.unix(classStartTimestamp)
    return dayjs.unix(currentTime).isAfter(classStartTime) || dayjs.unix(currentTime).isSame(classStartTime)
  }

  // 예약 변경 가능 여부
  const canChangeReservation = (classStartTimestamp: number, cutoffHours: number = 1): boolean => {
    const classStartTime = dayjs.unix(classStartTimestamp)
    const changeDeadline = classStartTime.subtract(cutoffHours, 'hours')
    return dayjs.unix(currentTime).isBefore(changeDeadline)
  }

  // 레슨 시작 여부
  const isLessonStarted = (classStartTimestamp: number): boolean => {
    const classStartTime = dayjs.unix(classStartTimestamp)
    return dayjs.unix(currentTime).isAfter(classStartTime)
  }

  useEffect(() => {
    if (enabled) {
      start()
    } else {
      stop()
    }

    return () => {
      stop()
    }
  }, [enabled, interval, start])

  // 컴포넌트 언마운트 시 cleanup
  useEffect(() => {
    return () => {
      stop()
    }
  }, [])

  return {
    currentTime,
    currentDate,
    start,
    stop,
    canEnterClass,
    canChangeReservation,
    isLessonStarted,
  }
}
